import { useEffect, useState, useMemo } from "react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import Nav from "@/components/Nav";
import { listQuoteRequests, updateQuoteStatus, getStats } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Mail, Phone, Calendar, Users, Wallet, MapPin, Utensils, Plane, Car } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { value: "all", label: "Tous statuts" },
  { value: "nouveau", label: "Nouveau" },
  { value: "en_cours", label: "En cours" },
  { value: "envoye", label: "Envoyé" },
  { value: "ferme", label: "Fermé" },
];

const STATUS_LABEL = {
  nouveau: { label: "Nouveau", cls: "bg-[#1A1B18] text-white" },
  en_cours: { label: "En cours", cls: "bg-[#96836B] text-white" },
  envoye: { label: "Envoyé", cls: "bg-[#E4DCCF] text-[#1A1B18]" },
  ferme: { label: "Fermé", cls: "bg-[#EAE8E3] text-[#737373]" },
};

const MEAL_PLAN_LABELS = {
  RO: "RO — Sans repas",
  BB: "BB — Petit-déjeuner",
  DP: "Demi-pension",
  PC: "Pension complète",
  AI: "All Inclusive",
};

export default function Admin() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([
        listQuoteRequests(status !== "all" ? { status } : {}),
        getStats(),
      ]);
      setItems(list);
      setStats(s);
    } catch {
      toast.error("Impossible de charger les demandes.");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [status]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (i) =>
        i.destination.toLowerCase().includes(q) ||
        i.full_name.toLowerCase().includes(q) ||
        i.email.toLowerCase().includes(q)
    );
  }, [items, search]);

  const onStatusChange = async (id, newStatus) => {
    try {
      const updated = await updateQuoteStatus(id, newStatus);
      setItems((prev) => prev.map((p) => (p.id === id ? updated : p)));
      if (selected?.id === id) setSelected(updated);
      toast.success("Statut mis à jour.");
      getStats().then(setStats);
    } catch {
      toast.error("Échec de la mise à jour.");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Nav />
      <main className="pt-32 pb-24 px-6 md:px-12 max-w-[1400px] mx-auto">
        <header className="flex items-end justify-between flex-wrap gap-6 border-b border-[#EAE8E3] pb-8">
          <div>
            <p className="label-eyebrow">Espace conseiller</p>
            <h1 className="font-serif-display text-4xl md:text-5xl mt-3">Demandes de devis</h1>
          </div>
          {stats && (
            <div className="flex gap-8" data-testid="admin-stats">
              <Stat label="Total" value={stats.total} />
              <Stat label="Nouveaux" value={stats.by_status.nouveau} />
              <Stat label="En cours" value={stats.by_status.en_cours} />
              <Stat label="Envoyés" value={stats.by_status.envoye} />
            </div>
          )}
        </header>

        <div className="flex flex-wrap gap-4 items-center my-8">
          <Input
            data-testid="admin-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par destination, nom, email…"
            className="luxury-input max-w-md"
          />
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="admin-filter-status" className="w-48 rounded-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white border border-[#EAE8E3]">
          <Table data-testid="admin-table">
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Destination</TableHead>
                <TableHead>Dates</TableHead>
                <TableHead>Voyageurs</TableHead>
                <TableHead>Budget</TableHead>
                <TableHead>Reçu</TableHead>
                <TableHead>Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-12 text-[#737373]">Chargement…</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-12 text-[#737373]">Aucune demande pour ce filtre.</TableCell></TableRow>
              ) : (
                filtered.map((r) => (
                  <TableRow
                    key={r.id}
                    data-testid={`admin-row-${r.id}`}
                    className="cursor-pointer hover:bg-[#FAF9F6]"
                    onClick={() => setSelected(r)}
                  >
                    <TableCell>
                      <div className="font-medium">{r.full_name}</div>
                      <div className="text-xs text-[#737373]">{r.email}</div>
                    </TableCell>
                    <TableCell>{r.destination}</TableCell>
                    <TableCell className="text-sm">
                      {format(parseISO(r.check_in), "d MMM", { locale: fr })} → {format(parseISO(r.check_out), "d MMM yyyy", { locale: fr })}
                    </TableCell>
                    <TableCell className="text-sm">{r.adults} ad. {r.children > 0 ? `· ${r.children} enf.` : ""}</TableCell>
                    <TableCell>{Number(r.budget).toLocaleString("fr-FR")} {r.currency}</TableCell>
                    <TableCell className="text-xs text-[#737373]">{format(parseISO(r.created_at), "d MMM, HH:mm", { locale: fr })}</TableCell>
                    <TableCell>
                      <Badge className={`rounded-none ${STATUS_LABEL[r.status].cls}`}>{STATUS_LABEL[r.status].label}</Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </main>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent data-testid="admin-detail-sheet" className="bg-white sm:max-w-xl overflow-y-auto">
          {selected && (
            <>
              <SheetHeader>
                <p className="label-eyebrow">Référence #{selected.id.slice(0, 8).toUpperCase()}</p>
                <SheetTitle className="font-serif-display text-3xl mt-2">{selected.destination}</SheetTitle>
              </SheetHeader>
              <div className="mt-8 space-y-6">
                <Row icon={Mail} label="Email"><a href={`mailto:${selected.email}`} className="hover:text-[#96836B]">{selected.email}</a></Row>
                {selected.phone && <Row icon={Phone} label="Téléphone">{selected.phone}</Row>}
                <Row icon={Calendar} label="Dates">{format(parseISO(selected.check_in), "d MMMM yyyy", { locale: fr })} → {format(parseISO(selected.check_out), "d MMMM yyyy", { locale: fr })}</Row>
                <Row icon={Users} label="Voyageurs">{selected.adults} adulte(s){selected.children > 0 ? ` · ${selected.children} enfant(s)` : ""}</Row>
                <Row icon={Wallet} label="Budget">{Number(selected.budget).toLocaleString("fr-FR")} {selected.currency}</Row>
                {selected.trip_type && <Row icon={MapPin} label="Type">{selected.trip_type}</Row>}
                {selected.departure_city && <Row icon={Plane} label="Ville de départ">{selected.departure_city}</Row>}
                {selected.meal_plan && <Row icon={Utensils} label="Pension">{MEAL_PLAN_LABELS[selected.meal_plan] || selected.meal_plan}</Row>}
                {selected.flights && <Row icon={Plane} label="Vols">{selected.flights}</Row>}
                {selected.transfers && <Row icon={Car} label="Transferts">{selected.transfers}</Row>}
                {selected.message && (
                  <div>
                    <p className="label-eyebrow">Message</p>
                    <p className="mt-3 text-sm leading-relaxed whitespace-pre-line bg-[#FAF9F6] border border-[#EAE8E3] p-4">{selected.message}</p>
                  </div>
                )}
                <div className="border-t border-[#EAE8E3] pt-6">
                  <p className="label-eyebrow">Statut</p>
                  <Select value={selected.status} onValueChange={(v) => onStatusChange(selected.id, v)}>
                    <SelectTrigger data-testid="detail-status-select" className="mt-3 rounded-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.filter((s) => s.value !== "all").map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.22em] text-[#737373]">{label}</p>
    <p className="font-serif-display text-3xl mt-1">{value}</p>
  </div>
);

const Row = ({ icon: Icon, label, children }) => (
  <div className="flex gap-4 items-start">
    <Icon className="h-4 w-4 mt-1 text-[#96836B]" strokeWidth={1.5} />
    <div className="flex-1">
      <p className="label-eyebrow">{label}</p>
      <div className="mt-1 text-sm">{children}</div>
    </div>
  </div>
);
